/**
 * Credit application page ([data-tx-credit]).
 *
 *   • progress rail — counts required fields per [data-sec] section
 *     inside the CF7 form, updates the § A/B/C rows, the REQUIRED
 *     FIELDS total and the bar
 *   • active section — IntersectionObserver highlights the rail row
 *   • jump buttons ([data-credit-jump]) scroll to a section
 *   • submitted states — CF7's wpcf7mailsent swaps each form for its
 *     "received" panel
 *   • § D drop zone — drag/drop onto the file input, filename readout,
 *     submit enabled only once a file is attached
 */
const REQUIRED = '[aria-required="true"], .wpcf7-acceptance input[type="checkbox"]';

const initCredit = () => {
    const root = document.querySelector('[data-tx-credit]');
    if (!root) return;

    /* ---------- application form + rail ---------- */
    const formWrap = root.querySelector('[data-credit-form]');
    const form     = formWrap ? formWrap.querySelector('form.wpcf7-form') : null;
    const rows     = Array.from(root.querySelectorAll('[data-credit-row]'));
    const status   = root.querySelector('[data-credit-status]');
    const reqOut   = root.querySelector('[data-credit-req]');
    const bar      = root.querySelector('[data-credit-bar]');
    let submitted  = false;

    const isFilled = (el) => (el.type === 'checkbox' ? el.checked : el.value.trim() !== '');

    const progress = () => {
        const out = {};
        if (!form) return out;
        form.querySelectorAll('[data-sec]').forEach((sec) => {
            const els = Array.from(sec.querySelectorAll(REQUIRED));
            out[sec.dataset.sec] = [els.filter(isFilled).length, els.length];
        });
        return out;
    };

    const render = () => {
        const p   = progress();
        const tot = ['a', 'b', 'c'].reduce((s, k) => s + ((p[k] || [0, 0])[1]), 0);
        const fil = ['a', 'b', 'c'].reduce((s, k) => s + ((p[k] || [0, 0])[0]), 0);
        const pct = submitted ? 100 : tot ? Math.round((fil / tot) * 100) : 0;

        rows.forEach((row) => {
            const id  = row.dataset.creditRow;
            const out = row.querySelector('[data-credit-count]');
            if (!out) return;
            const sec = p[id];
            if (!sec) return; // § D — static OPTIONAL label
            const done = submitted || (sec[1] > 0 && sec[0] === sec[1]);
            out.textContent = submitted ? '✓ SENT' : (done ? '✓ ' : '') + sec[0] + ' / ' + sec[1];
            out.classList.toggle('is-done', done);
        });
        if (status) {
            status.textContent = submitted ? '● SUBMITTED' : pct + '% COMPLETE';
            status.style.color = submitted ? 'var(--ok)' : '';
        }
        if (reqOut) reqOut.textContent = (submitted ? tot : fil) + ' / ' + tot;
        if (bar) bar.style.width = pct + '%';
    };

    if (form) {
        form.addEventListener('input', render);
        form.addEventListener('change', render);
    }
    render();

    /* active section highlight */
    const sections = Array.from(root.querySelectorAll('[data-sec]'));
    if ('IntersectionObserver' in window && sections.length) {
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (!e.isIntersecting) return;
                rows.forEach((row) => row.classList.toggle('is-on', row.dataset.creditRow === e.target.dataset.sec));
            });
        }, { rootMargin: '-30% 0px -55% 0px' });
        sections.forEach((s) => io.observe(s));
    }

    /* jump */
    root.querySelectorAll('[data-credit-jump]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const target = root.querySelector('[data-sec="' + btn.dataset.creditJump + '"]');
            if (!target) return;
            const top = target.getBoundingClientRect().top + window.scrollY - 24;
            window.scrollTo({ top, behavior: 'smooth' });
        });
    });

    /* submitted → swap the form for its "received" panel */
    const swapOnSent = (wrapSel, bodySel, doneSel, after) => {
        const wrap = root.querySelector(wrapSel);
        if (!wrap) return;
        wrap.addEventListener('wpcf7mailsent', () => {
            const body = wrap.querySelector(bodySel);
            const done = wrap.querySelector(doneSel);
            if (body) body.hidden = true;
            if (done) done.hidden = false;
            if (after) after();
        });
    };
    swapOnSent('[data-credit-form]', '[data-credit-form-body]', '[data-credit-done]', () => { submitted = true; render(); });
    swapOnSent('[data-credit-tax]', '[data-credit-tax-body]', '[data-credit-tax-done]');

    /* ---------- § D drop zone ---------- */
    const drop = root.querySelector('[data-credit-drop]');
    if (drop) {
        const input  = drop.querySelector('input[type="file"]');
        const taxForm = drop.closest('form');
        const submit = taxForm ? taxForm.querySelector('.wpcf7-submit') : null;
        const k = drop.querySelector('[data-credit-drop-k]');
        const t = drop.querySelector('[data-credit-drop-t]');
        const s = drop.querySelector('[data-credit-drop-s]');
        const note = root.querySelector('[data-credit-drop-note]');
        const idle = { k: k && k.textContent, t: t && t.textContent, s: s && s.textContent, note: note && note.textContent };

        const readout = () => {
            const file = input && input.files && input.files[0];
            drop.classList.toggle('has-file', !!file);
            if (k) k.textContent = file ? '● FILE ATTACHED' : idle.k;
            if (t) t.textContent = file ? file.name : idle.t;
            if (s) s.textContent = file ? Math.max(1, Math.round(file.size / 1024)) + ' KB · CLICK TO REPLACE' : idle.s;
            if (note) note.textContent = file ? 'READY TO SEND.' : idle.note;
            if (submit) submit.disabled = !file;
        };

        if (input) input.addEventListener('change', readout);
        drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('is-over'); });
        drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
        drop.addEventListener('drop', (e) => {
            e.preventDefault();
            drop.classList.remove('is-over');
            if (!input || !e.dataTransfer || !e.dataTransfer.files.length) return;
            input.files = e.dataTransfer.files;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        readout();
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCredit);
} else {
    initCredit();
}
